import {
  ID,
  IDPrefixes,
  newTaskSuffix,
  rootId,
  SortingMode,
  Tag,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  untaggedId,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from '../prefix-id';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

export type BuildTagTreeOptions = {
  tasks: Task[];
  tags: Tag[];
  relationshipsMap: Record<ID, ID[]>;
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
  untaggedLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildTagTree = ({
  tasks,
  tags,
  relationshipsMap,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
  untaggedLabel,
  rootLabel,
  newTaskLabel,
}: BuildTagTreeOptions): Record<ID, TaskListTreeItem> => {
  const tagTreeItems = tags.reduce<Record<ID, TaskListTreeItem>>((acc, tag) => {
    acc[tag.id] = {
      id: tag.id,
      type: TaskListItemTypes.Tag,
      name: tag.name,
      children: [],
      isFolder: true,
    };
    return acc;
  }, {});

  tagTreeItems[untaggedId] = {
    id: untaggedId,
    type: TaskListItemTypes.Tag,
    name: untaggedLabel,
    children: [],
    isFolder: true,
  };

  const tagTreeIds = Object.keys(tagTreeItems);

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? tagTreeIds.reduce<ID[]>((acc, tagId) => {
          const tagOrder = customOrder?.[tagId] ?? [];
          acc.push(...tagOrder);
          return acc;
        }, [])
      : [];

  const tasksWithTags = tasks.map((task) => {
    const taskPrefixedId = createPrefixId(task.id, IDPrefixes.Task);
    const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
    const taskTagIds = relatedIds
      .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
      .map((id) => getPrefixedId(id));
    return { task, tagIds: taskTagIds };
  });

  const sortedTasks = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  });

  const allTreeItems = sortedTasks.reduce<Record<ID, TaskListTreeItem>>(
    (acc, task) => {
      const taskWithTags = tasksWithTags.find(
        ({ task: matched }) => matched.id === task.id,
      );
      const taskTagIds = taskWithTags?.tagIds ?? [];
      const resolvedTagIds = taskTagIds.filter((tagId) => acc[tagId]);

      if (!resolvedTagIds.length) {
        acc[untaggedId]?.children?.push(task.id);
      }

      resolvedTagIds.forEach((tagId) => {
        acc[tagId].children?.push(task.id);
      });

      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };

      return acc;
    },
    tagTreeItems,
  );

  if (!hideNewTaskRow) {
    tagTreeIds.forEach((tagId) => {
      const tagTreeItem = allTreeItems[tagId];
      if (!tagTreeItem) {
        return;
      }
      if (!tagTreeItem.children?.length && !showEmptyGroups) {
        return;
      }

      const newTaskItemId = `${tagId}${newTaskSuffix}`;
      tagTreeItem.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
  const topLevelTagIds = Object.keys(filteredTreeItems).filter(
    (id) => filteredTreeItems[id].type === TaskListItemTypes.Tag,
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelTagIds,
    },
    ...filteredTreeItems,
  };
};
